// Cliente HTTP del admin-portal.
//
// Todos los endpoints viven bajo /api/admin/* (proxeados por Vite al
// dev-server en :3001). Los que requieren auth leen el session_token del
// sessionStore y lo mandan como Bearer.
//
// IMPORTANT: este token no se persiste en localStorage en M9.1 — vive en
// memoria del modulo (sessionStore). Recargar la pagina pierde la session
// (el usuario se re-loguea). M9.1+: mover a cookie HttpOnly + flag Secure
// cuando haya deploy prod; localStorage no es adecuado para tokens de admin.

import type { AccessMap } from '@access';

/**
 * Clave del perfil de acceso de la cuenta (admin_roles.role_key). Los perfiles
 * se crean desde Equipo → Perfiles, asi que es un string abierto; `admin`
 * existe siempre y es inmutable.
 */
export type AdminRole = string;

interface ApiEnvelope<T> {
  ok?: boolean;
  data?: T;
  error?: string;
  error_description?: string;
  message?: string;
  request_id?: string;
}

export class AdminApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

/**
 * Base URL del API.
 *
 * Resolucion en orden:
 *   1. `import.meta.env.VITE_API_BASE` (build-time env, ej:
 *      `VITE_API_BASE=https://api.auth.lpfplay-dev.com`).
 *   2. Default `/api` → relative URLs. En dev local, Vite proxyea `/api/*`
 *      a `http://localhost:3001` (ver vite.config.ts).
 *
 * Cuando se setea via env, NO se aplica el prefijo `/api`. El SPA llama
 * al API directamente cross-origin (CORS habilitado en el API GW).
 */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

// ---------------------------------------------------------------------------
// Session store — in-memory. Se hidrata al load desde sessionStorage (que
// se limpia al cerrar la tab, safer que localStorage).
// ---------------------------------------------------------------------------

const SESSION_KEY = 'authsys-admin-session';

export interface AdminSession {
  session_token: string;
  expires_at: number;
  role: AdminRole;
  /** Nombre visible del perfil. Opcional: sesiones persistidas antes no lo traen. */
  role_name?: string;
  /**
   * Mapa seccion → nivel del perfil, copiado al login. Es lo que decide el
   * menu, las rutas y los botones. Opcional: una sesion persistida antes de
   * los perfiles no lo trae y se deduce del nombre (lib/access.ts).
   */
  access?: AccessMap;
  /**
   * Permisos granulares de la cuenta ({ danger_zone: true, … }). Opcional:
   * sesiones persistidas pre-permisos no lo traen (= sin permisos extra).
   */
  permissions?: Record<string, boolean>;
  display_name: string;
}

let currentSession: AdminSession | null = null;
const sessionListeners = new Set<(s: AdminSession | null) => void>();

function persistSession(s: AdminSession | null): void {
  try {
    if (s) sessionStorage.setItem(SESSION_KEY, JSON.stringify(s));
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // sessionStorage puede fallar en private mode — tratarlo como memoria pura.
  }
}

function readPersistedSession(): AdminSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdminSession;
    if (parsed.expires_at <= Math.floor(Date.now() / 1000)) {
      sessionStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function getSession(): AdminSession | null {
  if (currentSession) return currentSession;
  currentSession = readPersistedSession();
  return currentSession;
}

export function setSession(s: AdminSession | null): void {
  currentSession = s;
  persistSession(s);
  for (const fn of sessionListeners) fn(s);
}

export function subscribeSession(fn: (s: AdminSession | null) => void): () => void {
  sessionListeners.add(fn);
  return () => sessionListeners.delete(fn);
}

// ---------------------------------------------------------------------------
// HTTP helpers
// ---------------------------------------------------------------------------

async function fetchJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const session = getSession();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (session) headers['Authorization'] = `Bearer ${session.session_token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  } catch {
    // Fallo de red real (offline, DNS, CORS, server caido) → TypeError opaco.
    // Lo normalizamos a `network_error` para distinguirlo de un error del server.
    throw new AdminApiError('network_error', `network request to ${path} failed`, 0);
  }

  // 204 No Content → devolver undefined as T (caller sabe qué esperar).
  if (res.status === 204) return undefined as unknown as T;

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await res.json()) as ApiEnvelope<T>;
  } catch {
    throw new AdminApiError('network_error', `invalid JSON from ${path}`, res.status);
  }

  if (!res.ok) {
    // 401 con sesion activa → expiro. Limpiar para forzar re-login.
    if (res.status === 401 && session) {
      setSession(null);
    }
    const code = envelope.error ?? 'unknown_error';
    const message = envelope.error_description ?? envelope.message ?? res.statusText;
    throw new AdminApiError(code, message, res.status);
  }

  // Endpoints admin devuelven envelope { ok: true, data: {...}, request_id }.
  // Endpoints legacy (cualquier JSON root) tambien valen.
  if ('ok' in envelope && envelope.ok === true && 'data' in envelope) {
    return envelope.data as T;
  }
  return envelope as unknown as T;
}

// ---------------------------------------------------------------------------
// Admin auth endpoints
// ---------------------------------------------------------------------------

export async function adminLogin(email: string, password: string): Promise<AdminSession> {
  const session = await fetchJson<AdminSession>('/admin/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  setSession(session);
  return session;
}

export async function adminLogout(): Promise<void> {
  try {
    await fetchJson<void>('/admin/auth/logout', { method: 'POST' });
  } finally {
    setSession(null);
  }
}

export interface AdminProfile {
  admin_user_id: string;
  email: string | null;
  role: AdminRole;
  role_name: string;
  access: AccessMap;
  permissions: Record<string, boolean>;
  display_name: string;
  session_id: string;
  expires_at: number;
}

export async function adminMe(): Promise<AdminProfile> {
  return fetchJson<AdminProfile>('/admin/auth/me');
}

// ---------------------------------------------------------------------------
// M9.7 Self-service profile + change password
// ---------------------------------------------------------------------------

export async function updateMyProfile(displayName: string): Promise<void> {
  await fetchJson<unknown>('/admin/auth/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ display_name: displayName }),
  });
}

export async function changeMyPassword(
  currentPassword: string,
  newPassword: string,
): Promise<AdminSession> {
  // El backend revoca sesiones previas y emite una nueva. Se actualiza el
  // sessionStore con el token nuevo para que el SPA siga autenticado.
  const next = await fetchJson<AdminSession>('/admin/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  setSession(next);
  return next;
}

// ---------------------------------------------------------------------------
// M9.7 Admin user CRUD (gestion del backoffice)
// ---------------------------------------------------------------------------

export interface AdminUserListItem {
  admin_user_id: string;
  email: string;
  display_name: string;
  role: AdminRole;
  /** Nombre visible del perfil. */
  role_name: string;
  permissions: Record<string, boolean>;
  status: 'active' | 'suspended' | 'deactivated';
  last_login_at: string | null;
  created_at: string;
}

export async function listAdmins(): Promise<AdminUserListItem[]> {
  const res = await fetchJson<{ admins: AdminUserListItem[]; count: number }>(
    '/admin/admins',
  );
  return res.admins;
}

export interface CreateAdminBody {
  email: string;
  display_name: string;
  role: AdminRole;
  password?: string;
}

export interface CreateAdminResult {
  admin_user_id: string;
  email: string;
  display_name: string;
  role: AdminRole;
  password: string;                 // plaintext, mostrar al user 1 vez
  password_autogenerated: boolean;
}

export async function createAdmin(body: CreateAdminBody): Promise<CreateAdminResult> {
  return fetchJson<CreateAdminResult>('/admin/admins', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export interface PatchAdminBody {
  display_name?: string;
  role?: AdminRole;
  status?: 'active' | 'suspended';
  /** Reemplaza el objeto completo. El backend revoca las sesiones del target. */
  permissions?: Record<string, boolean>;
}

export async function patchAdmin(adminUserId: string, body: PatchAdminBody): Promise<void> {
  await fetchJson<unknown>(`/admin/admins/${encodeURIComponent(adminUserId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteAdmin(adminUserId: string): Promise<void> {
  await fetchJson<unknown>(`/admin/admins/${encodeURIComponent(adminUserId)}`, {
    method: 'DELETE',
  });
}

export async function resetAdminPassword(
  adminUserId: string,
): Promise<{ admin_user_id: string; password: string; sessions_revoked: number }> {
  return fetchJson(`/admin/admins/${encodeURIComponent(adminUserId)}/reset-password`, {
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// Perfiles de acceso (admin_roles)
// ---------------------------------------------------------------------------

export interface AdminRoleItem {
  role_key: string;
  display_name: string;
  description: string;
  access: AccessMap;
  /** Solo `admin`: no se edita ni se borra. */
  is_builtin: boolean;
  /** Cuentas que usan el perfil, en cualquier estado. */
  account_count: number;
  created_at: string;
  updated_at: string;
}

export async function listRoles(): Promise<AdminRoleItem[]> {
  const res = await fetchJson<{ roles: AdminRoleItem[]; count: number }>('/admin/roles');
  return res.roles;
}

export interface CreateRoleBody {
  role_key: string;
  display_name: string;
  description?: string;
  access: AccessMap;
}

export async function createRole(body: CreateRoleBody): Promise<AdminRoleItem> {
  return fetchJson<AdminRoleItem>('/admin/roles', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export interface PatchRoleBody {
  display_name?: string;
  description?: string;
  /** Reemplaza el mapa completo. El backend cierra las sesiones de quienes lo usan. */
  access?: AccessMap;
}

export async function patchRole(
  roleKey: string,
  body: PatchRoleBody,
): Promise<AdminRoleItem & { sessions_revoked: number }> {
  return fetchJson(`/admin/roles/${encodeURIComponent(roleKey)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteRole(roleKey: string): Promise<void> {
  await fetchJson<unknown>(`/admin/roles/${encodeURIComponent(roleKey)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// M9.2 Dashboard + partners list
// ---------------------------------------------------------------------------

export interface PartnerListItem {
  partner_id: string;
  display_name: string;
  status: 'active' | 'suspended' | 'offboarded';
}

export async function listPartners(): Promise<PartnerListItem[]> {
  const res = await fetchJson<{ partners: PartnerListItem[]; count: number }>(
    '/admin/partners',
  );
  return res.partners;
}

export interface DashboardAlert {
  level: 'info' | 'warning' | 'critical';
  code: string;
  message: string;
  /** Ruta del backoffice donde se resuelve. */
  href?: string;
}

export interface DashboardSnapshot {
  partner: PartnerListItem;
  subscriptions: {
    total_active: number;
    /** Activas en un plan con precio > 0. Puede faltar si la Lambda es vieja. */
    paid_active?: number;
    grace_period: number;
    expired_last_30d: number;
    breakdown_by_plan: Array<{ plan_code: string; plan_name: string; count: number }>;
  };
  payments_30d: {
    total_count: number;
    succeeded_count: number;
    failed_count: number;
    succeeded_amount: number;
    currency: string | null;
    /** Por cobro. */
    success_rate: number | null;
    /** Por persona: intentaron pagar / lograron pagar al menos una vez. */
    payers: { attempted: number; succeeded: number; success_rate: number | null };
  };
  /**
   * Personas que hoy pueden ver contenido pago, en cubetas disjuntas (pago >
   * cupon > Hall Pass). Opcionales: pueden faltar si la Lambda es vieja.
   */
  paid_access?: { total: number; paid: number; coupon: number; hall_pass: number };
  /** Por persona. Semana y dia en curso, hora de Argentina. */
  success_rates?: {
    historic: DashboardPayers;
    week: DashboardPayers;
    today: DashboardPayers;
  };
  billing?: {
    currency: string | null;
    /** Cobrado neto de anulaciones y reintegros (sale del ledger). */
    collected_total: number;
    collected_last_month: number;
    collected_this_month: number;
    /** Estimacion: renovaciones que vencen antes de fin de mes, si todas se cobran. */
    pending_this_month: { amount: number; subscriptions: number };
  };
  accounts: { total: number; verified: number; as_of: string };
  webhooks: { available: boolean; note: string };
  alerts: DashboardAlert[];
  generated_at: string;
}

export interface DashboardPayers {
  attempted: number;
  succeeded: number;
  success_rate: number | null;
}

export async function fetchDashboard(partnerId: string): Promise<DashboardSnapshot> {
  return fetchJson<DashboardSnapshot>(
    `/admin/dashboard/${encodeURIComponent(partnerId)}`,
  );
}

// ---------------------------------------------------------------------------
// M9.3 User browser + audit log
// ---------------------------------------------------------------------------

// `pending`: alta iniciada cuyo primer pago todavia no se acredito (o fue rechazado).
export type SubscriptionStatus = 'pending' | 'trialing' | 'active' | 'grace_period' | 'expired' | 'cancelled';

export interface AdminUserSummary {
  user_id: string;
  display_name: string | null;
  status: 'active' | 'blocked' | 'deactivated';
  created_at: string;
  email?: string | null;
  email_verified?: boolean;
  document_masked?: string | null;
  subscription_status?: SubscriptionStatus | null;
  partner_id?: string | null;
  /** Nombre y apellido separados. Los devuelve la búsqueda por nombre. */
  first_name?: string | null;
  last_name?: string | null;
}

/** Qué criterio dedujo el backend a partir de lo que se tecleó. */
export type SearchCriterion = 'email' | 'document' | 'user_id' | 'provider_lookup_id';

/**
 * Búsqueda multi-criterio: email, documento, user_id o ID de Line en un solo
 * campo. El backend deduce cuál es (ver searchUsersAdmin). Devuelve lista
 * porque un documento puede matchear varias cuentas.
 */
export async function searchUsers(
  term: string,
): Promise<{ criterion: SearchCriterion; users: AdminUserSummary[] }> {
  const res = await fetchJson<{ criterion: SearchCriterion; users: AdminUserSummary[] }>(
    `/admin/users?q=${encodeURIComponent(term)}`,
  );
  return { criterion: res.criterion, users: res.users ?? [] };
}

/**
 * Una página de la búsqueda por nombre. Los cursores son opacos: `next` null
 * significa que no hay más hacia adelante, `prev` null que es la primera
 * página. No hay total a propósito: el backend no cuenta las coincidencias
 * para que una búsqueda amplia cueste lo mismo que una estrecha.
 */
export interface UserNamePage {
  /** El término tal como lo normalizó el backend (minúsculas, sin tildes). */
  term: string;
  /** Orden alfabético por "apellido nombre". */
  users: AdminUserSummary[];
  page: { next: string | null; prev: string | null; size: number };
}

/**
 * Búsqueda por nombre: coincide por el comienzo del apellido, y del nombre
 * si se agrega. Tiene su propio endpoint porque no es exacta ni devuelve una
 * lista acotada por naturaleza: pagina por cursor y está limitada por minuto.
 */
export async function searchUsersByName(
  term: string,
  cursor?: { after?: string; before?: string },
): Promise<UserNamePage> {
  const qs = new URLSearchParams({ q: term });
  if (cursor?.after) qs.set('after', cursor.after);
  else if (cursor?.before) qs.set('before', cursor.before);
  const res = await fetchJson<UserNamePage>(`/admin/users/by-name?${qs.toString()}`);
  return {
    term: res.term ?? term,
    users: res.users ?? [],
    page: res.page ?? { next: null, prev: null, size: 50 },
  };
}

export interface AdminUserSubscription {
  subscription_id: string;
  partner_id: string;
  plan_id: string;
  plan_code: string | null;
  plan_name: string | null;
  status: SubscriptionStatus;
  started_at: string;
  current_period_start: string | null;
  current_period_end: string | null;
  grace_period_end: string | null;
  /** Fin de la ventana de gracia de verificación de email (migr. 0037). */
  verification_grace_until: string | null;
  cancelled_at: string | null;
  auto_renew: boolean;
  external_subscription_id: string | null;
  created_at: string;
  payment_provider: 'line' | 'mercadopago' | 'stripe';
  price_amount: string | null;
  price_currency: string | null;
  billing_period: 'monthly' | 'yearly' | 'weekly' | null;
  /** Estado del cobro recurrente. */
  next_retry_at: string | null;
  retry_count: number;
  charge_error_count: number;
  last_charge_error_at: string | null;
  last_charge_error_code: string | null;
  last_charge_error_message: string | null;
}

export interface AdminUserPayment {
  payment_id: string;
  subscription_id: string;
  partner_id: string;
  plan_id: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed' | 'refunded';
  payment_provider: 'line' | 'mercadopago' | 'stripe';
  external_payment_id: string | null;
  /**
   * Con que dato ubica la pasarela el cobro en su panel (Line:
   * IdentificadorCliente). Es lo que pide Line para revertir un pago. Null en
   * pagos que todavia no se consultaron; ausente en un backend anterior.
   */
  provider_lookup_id?: string | null;
  failure_code: string | null;
  created_at: string;
  /** Periodo que cubre el cobro (0053). Ausente en un backend anterior. */
  period_start?: string | null;
  period_end?: string | null;
  /**
   * Este cobro sobra de un par aplicado dos veces: se reintegra con nota de
   * credito. `kept_external_payment_id` es el que sostiene el plan.
   */
  refundable_duplicate?: {
    kept_external_payment_id: string | null;
  } | null;
  /**
   * El intento de este pago vive en la suscripcion que recibio el acceso (pago
   * reasignado), no en la de este cliente. No se muestra el intento: seria
   * filtrar datos de otra cuenta.
   */
  attempt_registered_elsewhere?: boolean;
}

export interface AdminUserInvoice {
  invoice_id: string;
  payment_id: string;
  subscription_id: string | null;
  status: InvoiceStatus;
  full_number: string | null;
  cae: string | null;
  pdf_url: string | null;
  total_amount: number;
  currency: string;
  created_at: string;
  /** Nota de credito que anula esta factura (0044). Ausente en backend viejo. */
  credit_note?: {
    status: string;
    full_number: string | null;
    cae: string | null;
    pdf_url: string | null;
  } | null;
}

export interface AdminDeviceSummary {
  device_id: string;
  device_type: 'smarttv' | 'mobile' | 'web' | 'tablet' | 'other';
  device_model: string | null;
  device_version: string | null;
  os: string | null;
  os_version: string | null;
  browser: string | null;
  partner_id: string;
  login_at: number;
  last_seen_at: number;
  ip_hash: string | null;
  user_agent: string | null;
  session_id: string | null;
  /** IP del último login. Null en dispositivos anteriores a la migración 0038. */
  ip: string | null;
  /** Provincia argentina o país, resuelto desde la IP. Null si no se pudo. */
  geo_region: string | null;
  geo_kind: 'provincia' | 'pais' | null;
}

export interface AdminTosAcceptance {
  partner_id: string;
  tos_version: string;
  accepted_at: string;
  ip: string | null;
}

export interface AdminAuditEntry {
  audit_id: string;
  occurred_at: string;
  actor_type: string;
  actor_id: string;
  target_type: string | null;
  target_id: string | null;
  action: string;
  reason: string | null;
  ip: string | null;
  user_agent: string | null;
  metadata: Record<string, unknown>;
}

export interface VerificationGraceState {
  subscription_id: string;
  partner_id: string;
  plan_id: string;
  subscription_status: SubscriptionStatus | 'pending';
  /**
   * ¿La ventana realmente habilita el login? Solo si la sub está viva — el gate
   * exige trialing/active/grace_period. Una ventana sobre una sub `pending`
   * existe en la tabla pero no deja entrar a nadie.
   */
  effective: boolean;
  grace_until: string | null;
  /**
   * Segundos que faltan medidos por el reloj de POSTGRES (negativo = vencida),
   * no por el del navegador. La pantalla lo ancla contra su propio reloj y
   * descuenta desde ahí, así el contador no depende de la hora del cliente.
   */
  seconds_remaining: number | null;
}

/** Un cobro a devolver, venga de un intento sin aplicar o de un par aplicado. */
export interface AdminUserRefund {
  subscription_id: string;
  kind: 'unapplied' | 'applied';
  external_payment_id: string;
  amount: string;
  currency: string;
  created_at: string;
  provider_lookup_id: string | null;
  /** Solo `applied`: el cobro que sostiene el plan. */
  kept_external_payment_id: string | null;
  invoice: {
    invoice_id: string;
    status: string;
    full_number: string | null;
    pdf_url: string | null;
  } | null;
}

export interface UserDetailResponse {
  user: {
    user_id: string;
    display_name: string | null;
    status: 'active' | 'blocked' | 'deactivated';
    email_verified: boolean;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
    phone: string | null;
    avatar_url: string | null;
    marketing_opt_in: boolean;
    blocked_reason: string | null;
    blocked_at: string | null;
    /** Documento del TITULAR DEL PAGO — puede no ser el usuario. Ver migración 0038. */
    document: string | null;
    document_type: string | null;
    document_captured_at: string | null;
    cognito_sub: string;
    created_at: string;
    updated_at: string;
  };
  partner_ids: string[];
  /**
   * Ventana de gracia de verificación de email del usuario (migr. 0037), o
   * `null` si nunca contrató nada. Es la sub relevante cross-partner: la viva
   * si la hay, si no la `pending`, si no la más reciente.
   */
  verification_grace: VerificationGraceState | null;
  subscriptions: AdminUserSubscription[];
  payments: AdminUserPayment[];
  /**
   * UNICA lista de plata a devolver: intentos aprobados sin aplicar
   * (`unapplied`, sin factura) y pares de cobros aplicados donde uno sobra
   * (`applied`, con factura y nota de credito). Opcional: backend anterior.
   */
  refunds?: AdminUserRefund[];
  /**
   * Intentos de pago del alta (cada apertura del formulario de Line), del mas
   * nuevo al mas viejo. Opcional: un backend anterior no lo manda.
   */
  checkout_attempts?: CheckoutAttempt[];
  invoices: AdminUserInvoice[];
  /**
   * Cupones de días gratis canjeados por este usuario y Hall Pass que se le
   * otorgaron (misma tabla). `kind` falta en un backend anterior = cupón.
   */
  coupons: Array<{
    coupon_id: string;
    kind?: 'coupon' | 'hall_pass';
    /** Null en un Hall Pass (no tiene código). */
    code: string | null;
    partner_id: string;
    plan_id: string;
    plan_name: string | null;
    free_days: number | null;
    free_hours?: number | null;
    redeemed_at: string | null;
    subscription_id: string | null;
    /** Falta en un backend anterior. `revoked` = canjeado y despues anulado. */
    status?: 'issued' | 'redeemed' | 'revoked';
    revoked_at?: string | null;
  }>;
  devices: AdminDeviceSummary[];
  tos_acceptances: AdminTosAcceptance[];
  recent_audit: Array<{
    audit_id: string;
    occurred_at: string;
    actor_type: string;
    actor_id: string;
    action: string;
    reason: string | null;
    metadata: Record<string, unknown>;
  }>;
}

export async function fetchUserDetail(userId: string): Promise<UserDetailResponse> {
  return fetchJson<UserDetailResponse>(`/admin/users/${encodeURIComponent(userId)}`);
}

/** Cierra UN dispositivo: revoca su sesión y lo saca de la lista. */
export async function adminDeleteUserDevice(
  userId: string,
  deviceId: string,
  partnerId: string,
  reason?: string,
): Promise<{ deleted: boolean; session_revoked: boolean }> {
  return fetchJson(
    `/admin/users/${encodeURIComponent(userId)}/devices/${encodeURIComponent(deviceId)}` +
      `?partner_id=${encodeURIComponent(partnerId)}`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    },
  );
}

export interface AuditQuery {
  target_type?: string;
  target_id?: string;
  actor_type?: 'partner' | 'user' | 'admin_user' | 'system' | 'self';
  actor_id?: string;
  action?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export async function fetchAuditLog(
  q: AuditQuery,
): Promise<{ entries: AdminAuditEntry[]; count: number; limit_applied: number }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return fetchJson(`/admin/audit${qs ? `?${qs}` : ''}`);
}

// ---------------------------------------------------------------------------
// M9.4 Jobs & DLQ
// ---------------------------------------------------------------------------

export interface SchedulerRun {
  audit_id: string;
  occurred_at: string;
  metadata: {
    summaries?: Array<{
      provider: string;
      attempted: number;
      succeeded: number;
      failed: number;
      skipped_lock: number;
      errored: number;
      /** Desglose de `errored` por codigo. Ausente en runs anteriores a 0040. */
      errors_by_code?: Record<string, number>;
    }>;
    completed_at?: string;
  };
}

/**
 * Suscripciones que no se estan cobrando por un error TECNICO del procesador
 * (credencial vencida, comercio mal configurado, endpoint caido). No son
 * tarjetas rechazadas: esas viven en el historial de pagos del usuario.
 */
export interface ChargeErrorGroup {
  partner_id: string;
  error_code: string;
  subscriptions: number;
  oldest_error_at: string | null;
  sample_message: string | null;
}

export interface QueueInfo {
  name: string;
  description: string;
  url: string | null;
  available: boolean;
  approximate_messages: number;
  approximate_in_flight: number;
  approximate_delayed: number;
  has_dlq: boolean;
  dlq_name: string | null;
  dlq_url: string | null;
  dlq_approximate_messages: number | null;
  error_message: string | null;
}

export interface JobsStatus {
  scheduler_runs: SchedulerRun[];
  /** Ausente si el backend todavia no tiene la migracion 0040. */
  charge_errors?: ChargeErrorGroup[];
  queues: QueueInfo[];
}

export async function fetchJobsStatus(): Promise<JobsStatus> {
  return fetchJson<JobsStatus>('/admin/jobs/status');
}

export async function redriveDlq(
  queueName: string,
  maxPerSecond?: number,
): Promise<{ task_handle: string; source_queue: string; dlq_name: string }> {
  return fetchJson(
    `/admin/jobs/dlq/${encodeURIComponent(queueName)}/redrive`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(maxPerSecond ? { max_per_second: maxPerSecond } : {}),
    },
  );
}

/**
 * Libera el freno de los cobros trabados por un error tecnico del procesador.
 * La proxima corrida del cron (cada 15 minutos) los vuelve a intentar.
 */
export async function retryChargeErrors(partnerId?: string): Promise<{ cleared: number }> {
  return fetchJson('/admin/jobs/charge-errors/retry', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(partnerId ? { partner_id: partnerId } : {}),
  });
}

// ---------------------------------------------------------------------------
// Cobros no realizados
// ---------------------------------------------------------------------------

/**
 * blocked: el procesador falla (no es la tarjeta). overdue: vencida sin
 * intento (el cobrador no la toma). grace: el banco rechazo, hay reintentos
 * agendados. expired: se quedo sin reintentos por falta de pago.
 */
export type PendingChargeCategory = 'blocked' | 'overdue' | 'grace' | 'expired';

export interface PendingCharge {
  category: PendingChargeCategory;
  subscription_id: string;
  user_id: string;
  display_name: string | null;
  partner_id: string;
  plan_name: string | null;
  price_amount: string | null;
  price_currency: string | null;
  status: string;
  payment_provider: string;
  current_period_end: string | null;
  next_retry_at: string | null;
  retry_count: number;
  charge_error_count: number;
  last_charge_error_at: string | null;
  last_charge_error_code: string | null;
  last_charge_error_message: string | null;
  last_failed_payment_at: string | null;
  last_failure_code: string | null;
  updated_at: string;
}

/**
 * Pago que la pasarela aprobo cuando la suscripcion ya estaba paga con otro
 * (intento `duplicate`): se le cobro de mas al cliente y hay que reintegrarlo.
 * NO es una suscripcion a cobrar: nunca lleva "Reintentar cobro".
 */
export interface DuplicateCharge {
  subscription_id: string;
  user_id: string;
  display_name: string | null;
  partner_id: string;
  plan_name: string | null;
  payment_provider: string;
  /** Id del pago en la pasarela (Line: `pay_…`). */
  payment_id: string;
  /**
   * Con que dato ubica la pasarela el pago en su panel (Line:
   * IdentificadorCliente): con este se pide el reintegro. Ausente en un
   * backend anterior.
   */
  provider_lookup_id?: string | null;
  amount: string;
  currency: string;
  reason: string | null;
  provider_status: string | null;
  created_at: string;
  detected_at: string | null;
}

/** Cobro duplicado que SI se aplico: dos cobros `succeeded` del mismo periodo. */
export interface AppliedDuplicateCharge {
  subscription_id: string;
  user_id: string;
  display_name: string | null;
  partner_id: string;
  plan_name: string | null;
  /** 'same_period' = reintegro automatico desde aca; 'legacy' = revisar. */
  kind: 'same_period' | 'legacy';
  /** El cobro a reintegrar (el mas nuevo del par). */
  payment_id: string;
  external_payment_id: string | null;
  provider_lookup_id: string | null;
  amount: string;
  currency: string;
  period_start: string | null;
  period_end: string | null;
  created_at: string;
  /** El otro cobro, que cubre el mismo periodo. */
  other_payment_id: string;
  other_external_payment_id: string | null;
}

/** Pago informado por la pasarela que no se pudo atribuir a una suscripcion. */
export interface UnmatchedProviderPayment {
  id: string;
  provider: string;
  partner_id: string;
  external_payment_id: string;
  reference: string | null;
  amount: string | null;
  currency: string | null;
  reason: 'missing_reference' | 'invalid_reference' | 'unknown_subscription' | 'partner_mismatch';
  received_at: string;
  resolved_at: string | null;
  resolution: string | null;
}

export interface PendingChargesResponse {
  items: PendingCharge[];
  /** Ausente en un backend anterior a la lista de duplicados. */
  duplicates?: DuplicateCharge[];
  applied_duplicates?: AppliedDuplicateCharge[];
  unmatched_payments?: UnmatchedProviderPayment[];
  totals: Record<PendingChargeCategory, number> & { duplicate?: number; unmatched?: number };
  generated_at: string;
}

export async function fetchPendingCharges(partnerId?: string): Promise<PendingChargesResponse> {
  const qs = partnerId ? `?partner_id=${encodeURIComponent(partnerId)}` : '';
  return fetchJson(`/admin/billing/pending-charges${qs}`);
}

/** Lo que devuelve un reintento manual. `kind` decide el mensaje al operador. */
export interface ChargeRetryResult {
  subscription_id: string;
  kind: 'succeeded' | 'declined' | 'error' | 'skipped_lock' | 'duplicate' | 'refused';
  message?: string;
  failure_code?: string;
  error_code?: string;
  new_status?: string;
  period_end?: string;
  amount?: number;
}

/**
 * Cobra YA una suscripcion. Un rechazo del banco no la empeora (no consume
 * reintento ni la expira); una expirada solo vuelve si el cobro pasa.
 */
export async function retrySubscriptionCharge(
  subscriptionId: string,
  reason?: string,
): Promise<ChargeRetryResult> {
  return fetchJson(`/admin/subscriptions/${encodeURIComponent(subscriptionId)}/retry-charge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

// ---------------------------------------------------------------------------
// M9.5 Partner / Plans / Clients CRUD
// ---------------------------------------------------------------------------

export interface BrandingPalette {
  primary: string;
  accent: string;
  background: string;
  surface: string;
  text: string;
  text_muted: string;
  error: string;
  // Opcional por compat: branding guardado antes de 2026-07 no lo tiene (el
  // login-portal cae a un verde default por tema).
  success?: string;
}

export interface BrandingConfig {
  display_name: string;
  logo_url: string;
  logo_dark_url?: string;
  favicon_url?: string;
  theme_mode: 'light' | 'dark' | 'auto';
  colors: BrandingPalette;
  colors_dark?: BrandingPalette;
  font_family: string;
  font_url?: string;
  tos_url?: string;
  privacy_url?: string;
  footer_html?: string;
  contest_active?: boolean;
  contest_name?: string;
  contest_terms_url?: string;
}

/**
 * Tipo de favorito requerido al signup. Vive en `partners.signup_required_favorites`
 * (jsonb, migration 0011). Cada entrada produce un dropdown buscable en el
 * form de alta del login portal.
 *
 *  - `title_field` / `image_field`: referencia a una key de `favorite_items`.
 *    Formato `"name"` (columna nativa) o `"details.<key>"` (path en details
 *    jsonb del item, donde el partner guarda valores como crest_url, etc).
 *  - `label`: override del nombre del tipo en el form. Si falta, el endpoint
 *    publico `/signup-config` devuelve el `favorite_types.name`.
 *  - `initial_limit`: cantidad de items a mostrar antes de que el usuario tipee.
 */
export interface SignupRequiredFavorite {
  favorite_type_id: string;
  title_field: string;
  image_field?: string;
  label?: string;
  initial_limit: number;
}

export interface PartnerDetail {
  partner_id: string;
  display_name: string;
  status: 'active' | 'suspended' | 'offboarded';
  issuer_url: string;
  webhook_url: string | null;
  webhook_secret_configured: boolean;
  /**
   * Última rotación del webhook secret, leída del audit log. `fingerprint` es
   * sha256(secreto)[0:16] — permite confirmar cuál es el secreto vigente sin
   * exponerlo ni tener que rotarlo de nuevo para "estar seguro". El partner
   * calcula el mismo valor con `printf '%s' "$SECRETO" | sha256sum | cut -c1-16`.
   * `null` si nunca se rotó desde el backoffice.
   */
  webhook_secret_last_rotation: {
    fingerprint: string | null;
    rotated_at: string;
    actor_id: string;
  } | null;
  access_token_ttl_seconds: number;
  refresh_token_ttl_seconds: number;
  entitlements_cache_ttl_seconds: number;
  payment_provider_default: 'line' | 'mercadopago' | 'stripe';
  payment_retry_policy: { maxRetries: number; backoffHours: number[] } | null;
  features: Record<string, unknown>;
  require_email_verification: boolean;
  inspector_skip_email_verification: boolean;
  branding: BrandingConfig | null;
  signup_required_favorites: SignupRequiredFavorite[];
  /**
   * Plan destacado en la pantalla de cambio de plan del usuario final: viene
   * preseleccionado para que pueda ir directo al botón. `null` = sin destacado
   * (la pantalla cae al plan disponible de mayor precio). Se setea en el tab
   * Plans.
   */
  recommended_plan_id: string | null;
  created_at: string;
  updated_at: string;
}

export async function fetchPartnerDetail(partnerId: string): Promise<PartnerDetail> {
  return fetchJson<PartnerDetail>(`/admin/partners/${encodeURIComponent(partnerId)}`);
}

export interface PartnerPatchBody {
  display_name?: string;
  status?: 'active' | 'suspended' | 'offboarded';
  webhook_url?: string | null;
  access_token_ttl_seconds?: number;
  refresh_token_ttl_seconds?: number;
  entitlements_cache_ttl_seconds?: number;
  payment_provider_default?: 'line' | 'mercadopago' | 'stripe';
  payment_retry_policy?: { maxRetries: number; backoffHours: number[] } | null;
  features?: Record<string, unknown>;
  branding?: BrandingConfig | null;
  signup_required_favorites?: SignupRequiredFavorite[];
  /** Danger Zone (requiere permiso danger_zone). */
  require_email_verification?: boolean;
  /** Danger Zone: excepción del Inspector (dev-only) al gate de arriba. */
  inspector_skip_email_verification?: boolean;
  /** Plan destacado. `null` lo saca. El backend valida que sea activo y del partner. */
  recommended_plan_id?: string | null;
}

export async function patchPartner(partnerId: string, body: PartnerPatchBody): Promise<void> {
  await fetchJson<{ partner_id: string }>(`/admin/partners/${encodeURIComponent(partnerId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Danger Zone — configuracion de medios de pago (framework declarativo).
// El form se auto-genera desde las definiciones que devuelve el GET; los
// campos `secret` son write-only (el backend solo dice is_set, nunca el valor).
// ---------------------------------------------------------------------------

export interface PaymentProviderFieldColumn {
  key: string;
  label: string;
  type: 'text' | 'select';
  options?: string[];
  placeholder?: string;
}

export interface PaymentProviderFieldDef {
  key: string;
  label: string;
  type: 'text' | 'url' | 'password' | 'select' | 'rows';
  required: boolean;
  secret: boolean;
  placeholder?: string;
  help?: string;
  options?: string[];
  /** Para type 'rows': columnas de la tabla editable. */
  columns?: PaymentProviderFieldColumn[];
  pattern?: string;
}

export interface PaymentProviderConfigEntry {
  provider: 'line' | 'mercadopago' | 'stripe';
  display_name: string;
  description: string;
  implemented: boolean;
  fields: PaymentProviderFieldDef[];
  is_default: boolean;
  /** Valores actuales de los campos NO secretos. */
  values: Record<string, string>;
  /** Estado de los campos secretos (jamas el valor). */
  secrets: Record<string, { is_set: boolean }>;
  /** Campos sin valor cargado pero cubiertos por env var legacy (transicion). */
  env_fallback_active: string[];
  /** Requeridos sin valor en ninguna fuente. */
  missing_required: string[];
}

export interface PartnerPaymentConfig {
  partner_id: string;
  payment_provider_default: 'line' | 'mercadopago' | 'stripe';
  providers: PaymentProviderConfigEntry[];
}

export async function fetchPartnerPaymentConfig(partnerId: string): Promise<PartnerPaymentConfig> {
  return fetchJson<PartnerPaymentConfig>(
    `/admin/partners/${encodeURIComponent(partnerId)}/payment-config`,
  );
}

// --- Facturación electrónica (ARCA / Facturante) -----------------------------

export interface InvoicingFieldDef {
  key: string;
  label: string;
  type: 'text' | 'url' | 'password' | 'number';
  required: boolean;
  /** Write-only: se puede cargar, nunca leer. */
  secret: boolean;
  placeholder?: string;
  help?: string;
}

export interface InvoicingProbe {
  ok: boolean;
  message: string;
  latency_ms?: number;
}

export interface PartnerInvoicingConfig {
  partner_id: string;
  enabled: boolean;
  fields: InvoicingFieldDef[];
  values: Record<string, string | number>;
  secrets_set: Record<string, boolean>;
  missing_required: string[];
  configured: boolean;
  valid: boolean;
  validation_error: string | null;
  probe: InvoicingProbe | null;
  changed_keys?: string[];
  /**
   * Alícuota de IVA del partner (%). No es un campo de Facturante: vive en la
   * tabla y decide el desglose neto/IVA del asiento contable aunque la
   * facturación esté apagada. Por eso viaja fuera de `values`.
   */
  vat_rate: number | null;
}

export async function fetchPartnerInvoicingConfig(
  partnerId: string,
  probe = false,
): Promise<PartnerInvoicingConfig> {
  return fetchJson<PartnerInvoicingConfig>(
    `/admin/partners/${encodeURIComponent(partnerId)}/invoicing-config${probe ? '?probe=true' : ''}`,
  );
}

export async function putPartnerInvoicingConfig(
  partnerId: string,
  body: {
    enabled?: boolean;
    values?: Record<string, string>;
    probe?: boolean;
    force?: boolean;
    vat_rate?: number;
  },
): Promise<PartnerInvoicingConfig> {
  return fetchJson<PartnerInvoicingConfig>(
    `/admin/partners/${encodeURIComponent(partnerId)}/invoicing-config`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export interface PutPaymentConfigResult {
  partner_id: string;
  provider: string;
  updated_keys: string[];
  cleared_keys: string[];
  secret_keys_set: string[];
  payment_provider_default: string;
}

/**
 * Guarda valores de un provider. `values`: string no vacio = set; '' o null =
 * borra (plano) / ignora (secreto). Solo mandar lo que cambio.
 */
export async function putPartnerPaymentConfig(
  partnerId: string,
  provider: string,
  values: Record<string, string | null>,
  setAsDefault?: boolean,
): Promise<PutPaymentConfigResult> {
  return fetchJson<PutPaymentConfigResult>(
    `/admin/partners/${encodeURIComponent(partnerId)}/payment-config/${encodeURIComponent(provider)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ values, ...(setAsDefault ? { set_as_default: true } : {}) }),
    },
  );
}

export interface PartnerCreateBody {
  partner_id: string;
  display_name: string;
  payment_provider_default?: 'line' | 'mercadopago' | 'stripe';
  webhook_url?: string | null;
  access_token_ttl_seconds?: number;
  refresh_token_ttl_seconds?: number;
  entitlements_cache_ttl_seconds?: number;
}

/**
 * Crea un partner nuevo. Devuelve las dos API keys plaintext UNA SOLA VEZ —
 * la UI debe mostrarlas al admin con clipboard + warning de que no se vuelven
 * a mostrar. Si se pierden, hay que rotar.
 */
export interface PartnerCreateResponse {
  partner_id: string;
  issuer_url: string;
  api_key: string;
  api_key_admin: string;
  /** Secreto HMAC S2S. null si Secrets Manager falló — ver `warnings`. */
  api_secret: string | null;
  /** Claves de firma generadas en el alta. null si falló el provisioning. */
  jwks: PartnerJwksStatus | null;
  /** No vacío => el partner quedó creado pero incompleto; reintentar desde el detalle. */
  warnings: string[];
}

export async function createPartnerApi(body: PartnerCreateBody): Promise<PartnerCreateResponse> {
  return fetchJson<PartnerCreateResponse>('/admin/partners', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Limites estructurados del plan (`plans.limits` jsonb, migration 0010).
 * Conventions:
 *   - `null` o key ausente → ilimitado.
 *   - `0` → bloqueado.
 *   - `> 0` → limite concreto.
 */
export interface PlanLimits {
  max_favorites?: number | null;
  max_favorites_per_type?: number | null;
}

/** Bullet de la pantalla de planes. Espeja FeatureSchema del backend. */
export type PlanFeature = string | { label: string; included?: boolean };

/**
 * Espejo de la version vigente del plan (tabla `plans`). Sirve para mostrar.
 *
 * OJO: NO trae `features` (el endpoint de listado no las devuelve). Por eso
 * NUNCA construyas el body de publishPlanVersion a partir de un AdminPlan:
 * mandarias features: [] y borrarias las del plan sin querer. Para publicar,
 * partir SIEMPRE de un PlanVersion, que si trae el snapshot completo.
 */
export interface AdminPlan {
  plan_id: string;
  partner_id: string;
  code: string;
  display_name: string;
  price_amount: number;
  price_currency: string;
  billing_period: 'monthly' | 'yearly' | 'weekly';
  max_concurrent_streams: number;
  max_devices: number;
  trial_days: number;
  status: 'active' | 'archived';
  limits?: PlanLimits;
}

export async function listPartnerPlans(partnerId: string): Promise<AdminPlan[]> {
  const res = await fetchJson<{ plans: AdminPlan[] }>(
    `/admin/partners/${encodeURIComponent(partnerId)}/plans`,
  );
  return res.plans;
}

/**
 * `features` va aparte de AdminPlan a proposito: AdminPlan es el espejo que
 * devuelve el LISTADO, y ese endpoint no las trae (ver el comentario de arriba).
 * En el ALTA si se pueden mandar, y el backend las acepta desde siempre — lo
 * que faltaba era el editor en la UI.
 */
export type CreatePlanBody = Omit<AdminPlan, 'partner_id' | 'status'> & {
  features?: PlanFeature[];
};

export async function createPlanApi(
  partnerId: string,
  body: CreatePlanBody,
): Promise<{ plan_id: string }> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/plans`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Solo archiva/reactiva. Precio y config ya NO se cambian por PATCH: el
 * backend responde 409 si llegan (services/admin-plans-patch). Para cambiar
 * precio/config hay que publicar una version: ver publishPlanVersion().
 *
 * El tipo esta angostado a proposito — antes aceptaba todo AdminPlan y era
 * lo que permitia pisar el precio in-place sin dejar historia.
 */
export async function patchPlan(
  planId: string,
  body: { status: 'active' | 'archived' },
): Promise<void> {
  await fetchJson<{ plan_id: string }>(`/admin/plans/${encodeURIComponent(planId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Versionado de planes
//
// plan_versions es la fuente de verdad del precio; AdminPlan (tabla plans) es
// un espejo de la version vigente. Cambiar un precio = publicar una version.
// ---------------------------------------------------------------------------

export interface PlanVersion {
  plan_version_id: string;
  version: number;
  code: string;
  display_name: string;
  price_amount: number;
  price_currency: string;
  billing_period: 'monthly' | 'yearly' | 'weekly';
  max_concurrent_streams: number;
  max_devices: number;
  trial_days: number;
  features: PlanFeature[];
  limits: PlanLimits;
  valid_from: string;
  /** null = version vigente. */
  valid_until: string | null;
  is_current: boolean;
  published_by: string;
  published_at: string;
  change_reason: string | null;
  prev_version_id: string | null;
  /** Hash de la cadena de auditoria: prueba de que la fila no se toco. */
  row_hash: string;
}

export async function listPlanVersions(planId: string): Promise<PlanVersion[]> {
  const res = await fetchJson<{ plan_id: string; versions: PlanVersion[] }>(
    `/admin/plans/${encodeURIComponent(planId)}/versions`,
  );
  return res.versions;
}

export interface PublishPlanVersionBody {
  display_name: string;
  price_amount: number;
  price_currency: string;
  billing_period: 'monthly' | 'yearly' | 'weekly';
  max_concurrent_streams: number;
  max_devices: number;
  trial_days: number;
  features: PlanFeature[];
  limits: PlanLimits;
  /** Obligatorio, min 8 chars. Sin motivo, la historia no sirve para auditar. */
  change_reason: string;
  /** Optimistic locking: si otro admin publico en el medio, el backend da 409. */
  expected_current_version?: number;
}

export interface PublishPlanVersionResult {
  plan_id: string;
  plan_version_id: string;
  version: number;
  valid_from: string;
  closed_version: number | null;
  row_hash: string;
}

export async function publishPlanVersion(
  planId: string,
  body: PublishPlanVersionBody,
): Promise<PublishPlanVersionResult> {
  return fetchJson(`/admin/plans/${encodeURIComponent(planId)}/versions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Soft-delete: marca plan como archived. PATCH a status='active' lo recupera. */
export async function deletePlan(planId: string): Promise<void> {
  await fetchJson<{ plan_id: string; status: string }>(
    `/admin/plans/${encodeURIComponent(planId)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Tags de entitlements (migration 0034)
//
// Dos niveles:
//   - CATALOGO por partner (`partner_tags`): el tag se define una vez, con su
//     nombre visible y color. Es lo que alimenta el selector y el modal.
//   - ASIGNACION por plan (`plan_tags`): que tags tiene cada plan, con ventana
//     de vigencia opcional para promos.
//
// A diferencia de precio/features/limits, los tags NO son versionados: se
// guardan al toque, sin publicar una version ni pedir motivo. La trazabilidad
// la da el audit_log del backend.
// ---------------------------------------------------------------------------

export interface PartnerTag {
  partner_id: string;
  /** Slug estable. Es lo unico que viaja a la OTT. */
  tag: string;
  display_name: string;
  description: string | null;
  /** Hex de 6 digitos o null. Solo cosmetico. */
  color: string | null;
  status: 'active' | 'archived';
  /** Cuantos planes lo usan. 0 = se puede borrar; >0 = solo archivar. */
  plan_count: number;
}

/** Asignacion de un tag a un plan. null en las fechas = sin restriccion. */
export interface PlanTagAssignment {
  tag: string;
  valid_from: string | null;
  valid_until: string | null;
}

/** Asignacion + metadata del catalogo, para pintar el chip sin otro fetch. */
export interface PlanTagDetail extends PlanTagAssignment {
  display_name: string;
  description: string | null;
  color: string | null;
  status: 'active' | 'archived';
}

export async function listPartnerTags(partnerId: string): Promise<PartnerTag[]> {
  const res = await fetchJson<{ tags: PartnerTag[] }>(
    `/admin/partners/${encodeURIComponent(partnerId)}/tags`,
  );
  return res.tags;
}

export async function createPartnerTag(
  partnerId: string,
  body: { tag: string; display_name: string; description?: string | null; color?: string | null },
): Promise<{ tag: string }> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * `tag` en el body renombra el slug — el backend propaga a todos los planes
 * via ON UPDATE CASCADE. Es breaking para la OTT si lo tiene hardcodeado; el
 * modal avisa antes de dejar hacerlo.
 */
export async function patchPartnerTag(
  partnerId: string,
  tag: string,
  body: {
    tag?: string;
    display_name?: string;
    description?: string | null;
    color?: string | null;
    status?: 'active' | 'archived';
  },
): Promise<{ tag: string }> {
  return fetchJson(
    `/admin/partners/${encodeURIComponent(partnerId)}/tags/${encodeURIComponent(tag)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

/** Borrado duro. 409 (`tag_in_use`) si algun plan lo referencia. */
export async function deletePartnerTag(partnerId: string, tag: string): Promise<void> {
  await fetchJson<{ deleted: boolean }>(
    `/admin/partners/${encodeURIComponent(partnerId)}/tags/${encodeURIComponent(tag)}`,
    { method: 'DELETE' },
  );
}

export async function listPlanTags(
  planId: string,
): Promise<{ partner_id: string; tags: PlanTagDetail[] }> {
  return fetchJson(`/admin/plans/${encodeURIComponent(planId)}/tags`);
}

/** Reemplaza el set COMPLETO de tags del plan (declarativo, como publicar). */
export async function setPlanTags(
  planId: string,
  tags: PlanTagAssignment[],
): Promise<{ plan_id: string; tags: PlanTagDetail[] }> {
  return fetchJson(`/admin/plans/${encodeURIComponent(planId)}/tags`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tags }),
  });
}

// ---------------------------------------------------------------------------
// Versionado de T&C (ADR 0008)
// ---------------------------------------------------------------------------

export interface AdminTosVersion {
  version: string;
  tos_url: string;
  content_hash: string | null;
  effective_at: string;
  grace_days: number;
  published_by: string;
  created_at: string;
}

export async function listPartnerTosVersions(
  partnerId: string,
): Promise<{ current_version: string | null; versions: AdminTosVersion[] }> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/tos-versions`);
}

export async function publishTosVersion(
  partnerId: string,
  body: { version: string; tos_url: string; grace_days: number; effective_at?: string; content_hash?: string },
): Promise<{ partner_id: string; version: string; effective_at: string; grace_days: number }> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/tos-versions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

// Overrides por-client de parametros del partner. Solo las claves presentes
// overridean; ausente = valor del partner. Espejo de ClientConfigOverridesSchema
// del backend (libs/common/client-config.ts). webhook_url null = "sin webhook".
// Puede traer claves que esta version del portal no conoce: el editor las
// conserva al guardar (ver lib/client-overrides.ts).
export interface ClientConfigOverrides {
  require_email_verification?: boolean;
  plans_enabled?: boolean;
  access_token_ttl_seconds?: number;
  refresh_token_ttl_seconds?: number;
  entitlements_cache_ttl_seconds?: number;
  webhook_url?: string | null;
  /** Vigencia (s) del enlace con id_token_hint de la plataforma. Ausente = sin limite. */
  subscribe_hint_max_age_seconds?: number;
}

export interface AdminClient {
  client_id: string;
  partner_id: string;
  client_type: 'public' | 'confidential';
  redirect_uris: string[];
  grant_types: string[];
  scopes_allowed: string[];
  token_endpoint_auth_method: 'none' | 'client_secret_basic' | 'client_secret_post';
  post_logout_redirect_uris: string[];
  // false = client restringido (login preset): oauth-token saltea PKCE.
  pkce_required: boolean;
  config_overrides: ClientConfigOverrides;
  has_secret: boolean;
}

export async function listPartnerClients(partnerId: string): Promise<AdminClient[]> {
  const res = await fetchJson<{ clients: AdminClient[] }>(
    `/admin/partners/${encodeURIComponent(partnerId)}/clients`,
  );
  return res.clients;
}

export interface ClientCreateBody {
  client_id: string;
  client_type: 'public' | 'confidential';
  client_secret?: string;
  redirect_uris: string[];
  grant_types: string[];
  scopes_allowed: string[];
  token_endpoint_auth_method: 'none' | 'client_secret_basic' | 'client_secret_post';
  post_logout_redirect_uris?: string[];
  pkce_required?: boolean;
}

export async function createClientApi(
  partnerId: string,
  body: ClientCreateBody,
): Promise<{ client_id: string; has_secret: boolean }> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/clients`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function patchClient(
  clientId: string,
  body: Partial<Pick<AdminClient, 'redirect_uris' | 'grant_types' | 'scopes_allowed' | 'token_endpoint_auth_method' | 'post_logout_redirect_uris' | 'pkce_required' | 'config_overrides'>>,
): Promise<void> {
  await fetchJson<{ client_id: string }>(`/admin/clients/${encodeURIComponent(clientId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteClientApi(clientId: string): Promise<void> {
  await fetchJson(`/admin/clients/${encodeURIComponent(clientId)}`, { method: 'DELETE' });
}

// --- Login presets (URL estatica de arranque para clients restringidos) ---
// Ver docs/architecture/login-presets.md.
export type PresetKind = 'login' | 'signup' | 'subscribe';

export interface LoginPreset {
  preset_id: string;
  client_id: string;
  partner_id: string;
  kind: PresetKind;
  redirect_uri: string;
  scope: string;
  plan_id: string | null;
  platform: string | null;
  audience: string | null;
  connection: string | null;
  ui_locales: string | null;
  prompt: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface LoginPresetUpsertBody {
  kind: PresetKind;
  redirect_uri: string;
  scope: string;
  plan_id?: string;
  platform?: string;
  audience?: string;
  connection?: string;
  ui_locales?: string;
  prompt?: string;
  enabled: boolean;
}

export async function listClientPresets(clientId: string): Promise<LoginPreset[]> {
  const res = await fetchJson<{ presets: LoginPreset[] }>(
    `/admin/clients/${encodeURIComponent(clientId)}/presets`,
  );
  return res.presets;
}

export async function upsertClientPreset(
  clientId: string,
  presetId: string,
  body: LoginPresetUpsertBody,
): Promise<LoginPreset> {
  const res = await fetchJson<{ preset: LoginPreset }>(
    `/admin/clients/${encodeURIComponent(clientId)}/presets/${encodeURIComponent(presetId)}`,
    { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) },
  );
  return res.preset;
}

export async function deleteClientPreset(clientId: string, presetId: string): Promise<void> {
  await fetchJson(
    `/admin/clients/${encodeURIComponent(clientId)}/presets/${encodeURIComponent(presetId)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// M9.6 Admin destructive actions
// ---------------------------------------------------------------------------

export async function adminLogoutAll(
  userId: string,
  reason?: string,
): Promise<{ user_id: string; sessions_revoked: number }> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/logout-all`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export async function adminBlockUser(
  userId: string,
  reason: string,
): Promise<{ user_id: string; status: 'blocked'; sessions_revoked: number }> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/block`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export async function adminUnblockUser(
  userId: string,
  reason?: string,
): Promise<{ user_id: string; status: 'active' }> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/unblock`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

/**
 * Que paso con UN pago al reconciliar. Union cerrada a proposito: las
 * etiquetas viven en `lib/reconcile.ts` como `Record` completo, asi un
 * resultado nuevo del backend no compila hasta tener su texto.
 */
export type ReconcileOutcome =
  | 'activated'
  | 'already_active'
  | 'token_not_ready'
  | 'rejected'
  | 'duplicate'
  | 'provider_unavailable'
  | 'error'
  | 'invalid_payment_id';

export interface ReconcileResultRow {
  subscription_id: string;
  payment_id: string;
  outcome: ReconcileOutcome;
  /** Estado crudo de Line: CREATED, ACCEPTED, REJECTED, EXPIRED… */
  line_status?: string;
  /** Motivo del rechazo ("5 DENEGADA") o de la validacion del id manual. */
  message?: string;
}

export type CheckoutAttemptStatus =
  | 'open'
  | 'accepted'
  | 'rejected'
  | 'abandoned'
  | 'duplicate'
  /** Duplicado que alguien del equipo marco como reintegrado. */
  | 'refunded';

/** Un intento de pago del alta: cada vez que se abrio el formulario de Line. */
export interface CheckoutAttempt {
  subscription_id: string;
  payment_id: string;
  status: CheckoutAttemptStatus;
  provider_status: string | null;
  reason: string | null;
  amount: string;
  currency: string;
  source: 'checkout' | 'backfill' | 'manual';
  created_at: string;
  /** Ultima consulta a Line por este intento. */
  checked_at: string | null;
  /** Solo en `refunded`. Opcionales: un backend anterior no los manda. */
  refunded_at?: string | null;
  /** Lo que anoto el operador al marcarlo: n.º de operacion o comentario. */
  refund_reference?: string | null;
  /** Con que dato ubica la pasarela el pago en su panel (Line: IdentificadorCliente). */
  provider_lookup_id?: string | null;
}

export interface ReconcileResult {
  user_id: string;
  /** Intentos consultados contra Line. */
  scanned: number;
  activated: number;
  provider_unavailable: boolean;
  results: ReconcileResultRow[];
  /** Todos los intentos de las subs del usuario, del mas nuevo al mas viejo. */
  attempts: CheckoutAttempt[];
}

/**
 * Consulta a Line por los intentos de pago del usuario y acredita el que este
 * aprobado. Con `paymentId` consulta ademas ese pago puntual (`pay_<uuid>`),
 * para cuando el pago aprobado no figura entre los intentos conocidos.
 * Idempotente: un pago ya acreditado vuelve como `already_active`.
 */
export async function adminReconcileSubscription(
  userId: string,
  opts: { reason?: string; paymentId?: string } = {},
): Promise<ReconcileResult> {
  const body: { reason?: string; payment_id?: string } = {};
  if (opts.reason) body.reason = opts.reason;
  if (opts.paymentId) body.payment_id = opts.paymentId;
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/reconcile-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/**
 * Marca un cobro duplicado como reintegrado (mismo endpoint que Reconciliar,
 * con `action: 'mark_refunded'`). Exige Usuarios en nivel completo y el
 * permiso danger_zone. Devuelve los intentos de pago ya actualizados.
 */
export async function adminMarkAttemptRefunded(
  userId: string,
  paymentId: string,
  refundReference: string,
): Promise<{
  user_id: string;
  marked_refunded: { subscription_id: string; payment_id: string };
  attempts: CheckoutAttempt[];
}> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/reconcile-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'mark_refunded',
      payment_id: paymentId,
      refund_reference: refundReference,
    }),
  });
}

/**
 * Reintegro de un cobro duplicado que SI se aplico (cubria el mismo periodo que
 * otro cobro `succeeded`). A diferencia de `adminMarkAttemptRefunded`, aca el
 * backend mueve el registro contable: revierte el diferido, pasa el pago a
 * `refunded` y emite la Nota de Credito si la factura estaba autorizada.
 */
export async function adminRefundAppliedDuplicate(
  userId: string,
  paymentId: string,
  refundReference: string,
): Promise<{
  user_id: string;
  refunded: {
    payment_id: string;
    external_payment_id: string;
    paired_payment_id: string;
    amount: string;
    currency: string;
    already_refunded: boolean;
  };
  credit_note: { status: string; full_number?: string; error?: string } | null;
  attempts: CheckoutAttempt[];
}> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/reconcile-subscription`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      action: 'refund_applied_duplicate',
      payment_id: paymentId,
      refund_reference: refundReference,
    }),
  });
}

/** Cierra un pago que no se pudo atribuir a ninguna suscripcion. */
export async function adminResolveUnmatchedPayment(
  id: string,
  resolution: string,
): Promise<{ id: string; resolved: boolean }> {
  return fetchJson(`/admin/billing/unmatched-payments/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resolution }),
  });
}

/**
 * Duraciones del Hall Pass, en horas. El backend tiene la misma lista
 * (HALL_PASS_DURATIONS_HOURS en libs/db/queries/hall-pass.ts) y rechaza
 * cualquier otra con 400.
 */
export const HALL_PASS_OPTIONS: ReadonlyArray<{ hours: number; label: string }> = [
  { hours: 3, label: '3 horas' },
  { hours: 8, label: '8 horas' },
  { hours: 24, label: '1 día' },
  { hours: 96, label: '4 días' },
  { hours: 168, label: '7 días' },
  { hours: 360, label: '15 días' },
];

export function hallPassLabel(hours: number): string {
  return HALL_PASS_OPTIONS.find((o) => o.hours === hours)?.label ?? `${hours} h`;
}

/**
 * upgraded: se superpuso el plan del pase (el más caro del partner) mientras
 * rige; la suscripción NO se toca. renewed: ya tenía un Hall Pass y se re-ancló
 * desde ahora. kept_paid_plan: ya estaba en el plan del pase, así que el overlay
 * no agregaba acceso: solo se lo eximió de verificar el email. no_paid_plan: el
 * partner no tiene plan pago que dar.
 */
export type HallPassPlanOutcome = 'upgraded' | 'renewed' | 'kept_paid_plan' | 'no_paid_plan';

export interface HallPassResult {
  user_id: string;
  hours: number;
  subscription_id: string;
  partner_id: string;
  subscription_status: SubscriptionStatus | 'pending';
  /** false = la exención quedó escrita pero todavía no habilita el login. */
  effective: boolean;
  grace_until: string | null;
  seconds_remaining: number | null;
  previous_grace_until: string | null;
  plan_outcome: HallPassPlanOutcome;
  plan_id: string;
  plan_name: string | null;
  previous_plan_id: string;
  /** Hasta cuándo rige el plan del pase (el overlay). Null si no se superpuso nada. */
  access_until: string | null;
  /** Fila de `hall_passes`. Null si no se superpuso plan. */
  hall_pass_id: string | null;
}

/**
 * "Hall pass": por `hours` horas, exime al usuario de verificar el email y le
 * superpone el plan pago más caro del partner (salvo que ya esté en ese plan).
 * La suscripción NO se toca: el pase vive en `hall_passes` y al vencer el acceso
 * vuelve solo al estado real del usuario. Se registra además como un canje de
 * cupón, sin movimiento de dinero. 404 si no tiene ninguna suscripción.
 */
export async function adminGrantHallPass(
  userId: string,
  hours: number,
  reason?: string,
): Promise<HallPassResult> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/hall-pass`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason ? { hours, reason } : { hours }),
  });
}

/**
 * Baja de una suscripción desde el backoffice. Dos modos:
 *   - `end_of_period`: baja ordenada, conserva el acceso hasta el fin del
 *     período pago (igual que la baja del propio usuario). Pide Usuarios en
 *     nivel completo.
 *   - `immediate`: corta el acceso pago ya, aunque haya período pago por
 *     delante, y deja la cuenta en el plan gratuito del partner (si el partner
 *     no tiene plan gratuito, la suscripción queda expirada). No reintegra solo
 *     el período facturado. Pide además el permiso `danger_zone`.
 */
export type SubscriptionCancelMode = 'end_of_period' | 'immediate';

/**
 * `outcome` del backend. Baja ordenada: scheduled / already_scheduled /
 * switched_now. Corte inmediato: moved_to_free (quedó en el plan gratuito) /
 * expired (no había plan gratuito). `free_plan` y `not_found` no llegan acá:
 * el backend los responde como error.
 */
export type SubscriptionCancelOutcome =
  | 'scheduled'
  | 'already_scheduled'
  | 'switched_now'
  | 'moved_to_free'
  | 'expired';

export interface SubscriptionCancelResult {
  user_id: string;
  partner_id: string;
  subscription_id: string;
  mode: SubscriptionCancelMode;
  outcome: SubscriptionCancelOutcome;
  /** Estado en el que quedó la suscripción: `active` (plan gratuito) o `expired`. */
  status: 'active' | 'expired';
  auto_renew: boolean;
  /** `true` solo en la baja ordenada: conserva el acceso hasta el fin del período. */
  cancelled: boolean;
  /**
   * Hasta cuándo conserva el acceso. En la baja ordenada, el fin del período;
   * en el corte inmediato, el instante del corte (fin del acceso pago).
   */
  access_until: string | null;
}

export async function adminCancelSubscription(
  userId: string,
  opts: { partnerId: string; mode: SubscriptionCancelMode; reason: string },
): Promise<SubscriptionCancelResult> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/subscription/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      partner_id: opts.partnerId,
      mode: opts.mode,
      reason: opts.reason,
    }),
  });
}

// --- Investigación de entregabilidad de email (email-report) ----------------
// Endpoint de dos fases: start lanza las queries de Logs Insights (tardan
// 5-30 s) y poll espera los resultados. El modal del detalle de usuario polea.

export type EmailReportEventKind =
  | 'created' | 'send_verif' | 'send_reset' | 'resend' | 'fail'
  | 'verified' | 'reset_done' | 'login_reject' | 'login_ok';

export interface EmailReportEvent {
  ts: string;
  last_ts: string;
  kind: EmailReportEventKind;
  label: string;
  count: number;
  message_id?: string;
  used_after_sec?: number;
  invalidated?: boolean;
  error?: string;
}

export interface EmailReport {
  email: string | null;
  days: number;
  generated_at: string;
  verdict: {
    code: string;
    status: 'good' | 'warning' | 'serious' | 'critical';
    title: string;
    detail: string;
  };
  kpis: {
    mails_sent: number;
    mails_verification: number;
    mails_reset: number;
    send_failures: number;
    verified_at: string | null;
    email_verified_db: boolean;
    suppressed_reason: string | null;
  };
  timeline: EmailReportEvent[];
  identification: {
    user_id: string;
    cognito_sub: string;
    account_status: string;
    cognito_status: string | null;
    created: string | null;
    modified: string | null;
  };
  /** Emails de terceros: completos si el rol es admin, enmascarados si no. */
  twins: Array<{ email: string; status: string; created: string | null }>;
  twins_masked: boolean;
  /** Partner de la cuenta más antigua del user, con su logo (tema claro) inline. */
  partner: { partner_id: string; display_name: string; logo_data_uri: string | null } | null;
  infra: {
    sending_enabled: boolean;
    enforcement: string;
    quota_sent_24h: number;
    quota_max_24h: number;
    max_send_rate: number;
    domain: string | null;
    dkim: string | null;
    spf: boolean;
    dmarc_policy: string | null;
  };
  platform_24h: {
    email_sent: number | null;
    email_failed: number | null;
    abuse_throttled: number | null;
    /** Reputación SES (fracción 0-1, cuenta de envío). Umbrales AWS: 5%/10% y 0.1%/0.5%. */
    reputation_bounce_rate: number | null;
    reputation_complaint_rate: number | null;
  };
}

export type EmailReportPollResult =
  | { phase: 'running' }
  | { phase: 'done'; report: EmailReport };

export async function adminStartEmailReport(
  userId: string,
  days = 7,
): Promise<{ phase: 'started'; query_ids: { a: string; b: string }; days: number; email: string | null }> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/email-report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'start', days }),
  });
}

export async function adminPollEmailReport(
  userId: string,
  queryIds: { a: string; b: string },
  days = 7,
): Promise<EmailReportPollResult> {
  return fetchJson(`/admin/users/${encodeURIComponent(userId)}/email-report`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'poll', query_ids: queryIds, days }),
  });
}

export interface RotatedWebhookSecret {
  partner_id: string;
  webhook_secret: string;
  webhook_secret_hash_prefix: string;
  message: string;
}

export async function adminRotateWebhookSecret(partnerId: string): Promise<RotatedWebhookSecret> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/rotate-webhook-secret`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

// ---------------------------------------------------------------------------
// Credenciales del partner — claves de firma OIDC (JWKS) + api_secret HMAC
// ---------------------------------------------------------------------------

/** Un slot del JWKS. `alg` es null si no se pudo leer el secret. */
export interface PartnerJwksSlot {
  kid: string;
  alg: 'RS256' | 'ES256' | null;
}

export interface PartnerJwksStatus {
  /** false => el partner NO puede emitir id_token: el login OIDC devuelve 500. */
  configured: boolean;
  current: PartnerJwksSlot | null;
  /** Publicada en el JWKS pero NO usada para firmar. */
  secondary: PartnerJwksSlot | null;
  read_error: string | null;
}

export interface PartnerCredentials {
  partner_id: string;
  jwks: PartnerJwksStatus;
  api_secret_configured: boolean;
}

export type PartnerCredentialAction =
  | 'jwks_provision'
  | 'jwks_stage'
  | 'jwks_promote'
  | 'jwks_discard_secondary'
  | 'rotate_api_secret';

export interface PartnerCredentialActionResult extends PartnerCredentials {
  action: PartnerCredentialAction;
  /** Solo en rotate_api_secret: plaintext mostrado una única vez. */
  api_secret?: string;
  message?: string;
}

export async function fetchPartnerCredentials(partnerId: string): Promise<PartnerCredentials> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/credentials`);
}

export async function runPartnerCredentialAction(
  partnerId: string,
  action: PartnerCredentialAction,
): Promise<PartnerCredentialActionResult> {
  return fetchJson(`/admin/partners/${encodeURIComponent(partnerId)}/credentials`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action }),
  });
}

// ---------------------------------------------------------------------------
// Disposable email domains (3.29) — blacklist gestionable de mails desechables
// ---------------------------------------------------------------------------

export interface DisposableDomain {
  domain: string;
  added_by_admin_id: string | null;
  notes: string | null;
  created_at: string;
}

export async function listDisposableDomains(): Promise<{ domains: DisposableDomain[]; count: number }> {
  return fetchJson('/admin/disposable-domains');
}

export async function addDisposableDomain(
  domain: string,
  notes?: string,
): Promise<void> {
  await fetchJson<{ domain: string; added: true }>('/admin/disposable-domains', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domain, ...(notes && { notes }) }),
  });
}

export async function deleteDisposableDomain(domain: string): Promise<void> {
  await fetchJson<{ domain: string; removed: true }>(
    `/admin/disposable-domains/${encodeURIComponent(domain)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Favorites catalog (admin) — favorite_types + favorite_items
// ---------------------------------------------------------------------------

/**
 * Form schema declarado por un favorite_type. Define los campos custom que se
 * piden al crear un favorite_item de ese tipo. Vive en `favorite_types.details.form_schema`
 * (jsonb libre — no validado en backend; validamos solo en UI).
 *
 * Tipos soportados:
 *   - `string`   → input single-line
 *   - `text`     → textarea multiline
 *   - `url`      → input type=url
 *   - `number`   → input type=number
 *   - `boolean`  → checkbox
 *   - `date`     → input type=date
 */
export type FormFieldType = 'string' | 'text' | 'url' | 'number' | 'boolean' | 'date';

export interface FormSchemaField {
  key: string;        // slug usado como key en details.type_parameters
  label: string;      // display
  type: FormFieldType;
  required: boolean;
}

export interface FormSchema {
  fields: FormSchemaField[];
}

export type FavoriteTypeStatus = 'active' | 'archived';
export type FavoriteItemStatus = 'active' | 'archived';

export interface FavoriteType {
  favorite_type_id: string;
  partner_id: string;
  code: string;
  name: string;
  description: string | null;
  details: Record<string, unknown>;       // contiene `form_schema`, `icon`, `color`, etc.
  status: FavoriteTypeStatus;
}

export interface FavoriteItem {
  favorite_id: string;
  partner_id: string;
  favorite_type_id: string;
  code: string;
  name: string;
  description: string | null;
  details: Record<string, unknown>;       // contiene `type_parameters` con los valores del form
  status: FavoriteItemStatus;
}

// --- types ----------------------------------------------------------------

export async function listFavoriteTypes(partnerId: string): Promise<FavoriteType[]> {
  const res = await fetchJson<{ types: FavoriteType[]; count: number }>(
    `/admin/partners/${encodeURIComponent(partnerId)}/favorite-types`,
  );
  return res.types;
}

export interface CreateFavoriteTypeBody {
  favorite_type_id: string;               // 'ftype_<slug>'
  code: string;
  name: string;
  description?: string | null;
  details?: Record<string, unknown>;
}

export async function createFavoriteType(
  partnerId: string,
  body: CreateFavoriteTypeBody,
): Promise<{ favorite_type_id: string }> {
  return fetchJson(
    `/admin/partners/${encodeURIComponent(partnerId)}/favorite-types`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export interface PatchFavoriteTypeBody {
  name?: string;
  description?: string | null;
  details?: Record<string, unknown>;
  status?: FavoriteTypeStatus;
}

export async function patchFavoriteType(
  typeId: string,
  body: PatchFavoriteTypeBody,
): Promise<void> {
  await fetchJson<{ favorite_type_id: string }>(
    `/admin/favorite-types/${encodeURIComponent(typeId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

/** Soft-delete: marca el tipo como `archived`. */
export async function deleteFavoriteType(typeId: string): Promise<void> {
  await fetchJson<{ favorite_type_id: string; status: 'archived' }>(
    `/admin/favorite-types/${encodeURIComponent(typeId)}`,
    { method: 'DELETE' },
  );
}

// --- items ----------------------------------------------------------------

export interface ListFavoriteItemsParams {
  favorite_type_id?: string;
  status?: FavoriteItemStatus;
  cursor?: string;
  limit?: number;
}

export interface FavoriteItemsPage {
  items: FavoriteItem[];
  count: number;
  next_cursor: string | null;
}

export async function listFavoriteItems(
  partnerId: string,
  params: ListFavoriteItemsParams = {},
): Promise<FavoriteItemsPage> {
  const qs = new URLSearchParams();
  if (params.favorite_type_id) qs.set('favorite_type_id', params.favorite_type_id);
  if (params.status) qs.set('status', params.status);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const tail = qs.toString();
  return fetchJson<FavoriteItemsPage>(
    `/admin/partners/${encodeURIComponent(partnerId)}/favorite-items${tail ? `?${tail}` : ''}`,
  );
}

export async function getFavoriteItem(favoriteId: string): Promise<FavoriteItem> {
  const res = await fetchJson<{ item: FavoriteItem }>(
    `/admin/favorite-items/${encodeURIComponent(favoriteId)}`,
  );
  return res.item;
}

export interface CreateFavoriteItemBody {
  favorite_id?: string;                   // si se omite, backend genera fav_<ulid>
  favorite_type_id: string;
  code: string;
  name: string;
  description?: string | null;
  details?: Record<string, unknown>;
}

export async function createFavoriteItem(
  partnerId: string,
  body: CreateFavoriteItemBody,
): Promise<{ favorite_id: string }> {
  return fetchJson(
    `/admin/partners/${encodeURIComponent(partnerId)}/favorite-items`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export interface PatchFavoriteItemBody {
  favorite_type_id?: string;
  name?: string;
  description?: string | null;
  details?: Record<string, unknown>;
  status?: FavoriteItemStatus;
}

export async function patchFavoriteItem(
  favoriteId: string,
  body: PatchFavoriteItemBody,
): Promise<void> {
  await fetchJson<{ favorite_id: string }>(
    `/admin/favorite-items/${encodeURIComponent(favoriteId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

/** Soft-delete: marca el item como `archived`. user_favorites NO se borran. */
export async function deleteFavoriteItem(favoriteId: string): Promise<void> {
  await fetchJson<{ favorite_id: string; status: 'archived' }>(
    `/admin/favorite-items/${encodeURIComponent(favoriteId)}`,
    { method: 'DELETE' },
  );
}

// ---------------------------------------------------------------------------
// Cupones de días gratis — sección Cupones con niveles read < create < revoke.
// El listado se ve con `read`; emitir pide `create` y anular `revoke`.
// Los códigos de cupones VIGENTES viajan en claro solo para quien llega a
// `create`: cualquiera que lea un código `issued` puede canjearlo, así que a
// `read` el backend le manda `code: null` (ver admin-coupons-list). El panel
// dibuja el enmascarado en ese caso; el backend es quien decide.
// ---------------------------------------------------------------------------

export type CouponStatus = 'issued' | 'redeemed' | 'revoked';

export interface AdminCoupon {
  coupon_id: string;
  /**
   * `null` cuando el cupón sigue vigente (`issued`) y la sesión no llega a
   * `create`: un código canjeable no se revela al nivel de solo lectura. Los
   * cupones ya canjeados o anulados siempre vienen con su código.
   */
  code: string | null;
  partner_id: string;
  plan_id: string;
  plan_name: string | null;
  free_days: number;
  status: CouponStatus;
  batch_id: string;
  note: string | null;
  created_by: string;
  created_by_display_name: string | null;
  created_at: string;
  redeemed_by_user_id: string | null;
  redeemed_by_display_name: string | null;
  redeemed_at: string | null;
  subscription_id: string | null;
  revoked_by: string | null;
  revoked_at: string | null;
}

export interface CouponBatchResult {
  batch_id: string;
  partner_id: string;
  plan_id: string;
  plan_name: string;
  free_days: number;
  coupons: Array<{ coupon_id: string; code: string }>;
}

export async function createCoupons(input: {
  partner_id: string;
  plan_id: string;
  free_days: number;
  quantity: number;
  note?: string;
}): Promise<CouponBatchResult> {
  return fetchJson<CouponBatchResult>('/admin/coupons', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export async function listCoupons(q: {
  partner_id?: string;
  status?: CouponStatus;
  batch_id?: string;
  code?: string;
  limit?: number;
  offset?: number;
}): Promise<{ coupons: AdminCoupon[]; total: number }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return fetchJson(`/admin/coupons${qs ? `?${qs}` : ''}`);
}

/**
 * Qué pasó al anular (lo decide el backend bajo lock):
 *   revoked_unused                 — estaba sin usar: deja de ser canjeable.
 *   revoked_moved_to_free          — estaba canjeado y la suscripción vivía del
 *                                    cupón: quedó en el plan gratuito del partner.
 *   revoked_expired                — ídem, pero el partner no tiene plan
 *                                    gratuito: la suscripción quedó expirada.
 *   revoked_subscription_untouched — estaba canjeado pero la suscripción ya no
 *                                    depende del cupón (pagó, cambió de plan,
 *                                    venció…): se anuló el cupón y nada más.
 *   already_revoked                — ya estaba anulado (doble clic / reintento).
 */
export type CouponRevokeOutcome =
  | 'revoked_unused'
  | 'revoked_moved_to_free'
  | 'revoked_expired'
  | 'revoked_subscription_untouched'
  | 'already_revoked';

export interface CouponRevokeResult {
  coupon_id: string;
  status: 'revoked';
  outcome: CouponRevokeOutcome;
  /** Ausentes en `already_revoked`. */
  user_id?: string | null;
  partner_id?: string;
  subscription_id?: string | null;
  access_until?: string | null;
}

/** `reason` es obligatorio si el cupón ya fue canjeado (el backend lo exige). */
export async function revokeCoupon(
  couponId: string,
  reason?: string,
): Promise<CouponRevokeResult> {
  return fetchJson(`/admin/coupons/${encodeURIComponent(couponId)}/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

// ---------------------------------------------------------------------------
// Reportes financieros
//
// Todo sale del LEDGER (accounting_entries), no de payments. La distincion no
// es cosmetica: payments dice "entro plata"; el ledger dice cuanto de eso es
// IVA que no es tuyo, cuanto es deuda con el suscriptor por servicio no
// prestado, y cuanto ganaste de verdad.
// ---------------------------------------------------------------------------

export interface FinancialBucket {
  period: string;
  /** Cobrado que quedo (IVA incluido): ya sin cobros anulados ni reintegros. */
  gross_collected: number;
  vat: number;
  net_collected: number;
  /** Lo que REALMENTE ganaste: devengado por el paso del tiempo. */
  revenue_recognized: number;
  breakage: number;
  /** Saldo de ingresos diferidos al cierre: deuda con los suscriptores. */
  deferred_balance_end: number;
  /** Cobros anulados (nunca ocurrieron). Ya descontados de gross_collected. */
  voided_charges: number;
  /** Plata devuelta a clientes. Ya descontada de gross_collected. */
  refunds: number;
  gateway_fees: number;
  chargebacks: number;
  /** Operativo (de payments, no del ledger): un fallo no es un hecho economico. */
  charges_succeeded: number;
  charges_failed: number;
  success_rate: number | null;
}

export interface ReportChainStatus {
  chain_name: string;
  verified: boolean;
  last_seq: number | null;
  head_hash: string | null;
  detail: string | null;
}

export interface FinancialReport {
  params: {
    partner_id: string | null;
    from: string;
    to: string;
    group_by: 'day' | 'week' | 'month';
    currency: string;
  };
  buckets: FinancialBucket[];
  totals: Omit<FinancialBucket, 'period'>;
  /**
   * Snapshot a HOY (no al rango): cuentas con acceso gratis / pagando /
   * por cupón. Los cupones no suman ingresos — solo aparecen acá.
   */
  account_counts: {
    free_plan: number;
    paying: number;
    coupon: number;
  };
  /** Si balanced o chains_verified vienen en false, los numeros no son confiables. */
  reconciliation: {
    balanced: boolean;
    debit_total: number;
    credit_total: number;
    chains_verified: boolean;
    chains: ReportChainStatus[];
  };
  generated_at: string;
}

export async function getFinancialReport(q: {
  partner_id?: string | undefined;
  from: string;
  to: string;
  group_by: 'day' | 'week' | 'month';
  currency: string;
}): Promise<FinancialReport> {
  const p = new URLSearchParams({
    from: q.from,
    to: q.to,
    group_by: q.group_by,
    currency: q.currency,
  });
  if (q.partner_id) p.set('partner_id', q.partner_id);
  return fetchJson<FinancialReport>(`/admin/reports/financial?${p.toString()}`);
}

export interface TrialBalanceAccount {
  account_code: string;
  name: string;
  account_type: string;
  normal_side: string;
  debit_total: number;
  credit_total: number;
  balance: number;
}

export interface TrialBalance {
  as_of: string;
  currency: string;
  partner_id: string | null;
  accounts: TrialBalanceAccount[];
  totals: { debit: number; credit: number; balanced: boolean };
  generated_at: string;
}

export async function getTrialBalance(q: {
  partner_id?: string | undefined;
  as_of?: string | undefined;
  currency: string;
}): Promise<TrialBalance> {
  const p = new URLSearchParams({ currency: q.currency });
  if (q.partner_id) p.set('partner_id', q.partner_id);
  if (q.as_of) p.set('as_of', q.as_of);
  return fetchJson<TrialBalance>(`/admin/reports/trial-balance?${p.toString()}`);
}

// ---------------------------------------------------------------------------
// Facturación electrónica (Facturante / ARCA)
// ---------------------------------------------------------------------------

export type InvoiceStatus = 'pending' | 'sending' | 'submitted' | 'authorized' | 'rejected' | 'error';

export interface AdminInvoice {
  invoice_id: string;
  payment_id: string;
  subscription_id: string | null;
  partner_id: string;
  user_id: string;
  status: InvoiceStatus;
  external_id: string;
  facturante_internal_id: number | null;
  document_type: string | null;
  full_number: string | null;
  cae: string | null;
  cae_expires_at: string | null;
  pdf_url: string | null;
  currency: string;
  total_amount: number;
  net_amount: number | null;
  vat_amount: number | null;
  vat_rate: number;
  service_period_start: string;
  service_period_end: string;
  attempts: number;
  error_code: string | null;
  error_message: string | null;
  /** Ultimo lote en el que salio hacia Facturante. */
  batch_id: string | null;
  submitted_at: string | null;
  authorized_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Estado vivo en Facturante. Sólo se consulta si la factura no está resuelta. */
export interface InvoiceProviderState {
  found: boolean | null;
  state?: string;
  state_id?: number;
  full_number?: string | null;
  cae?: string | null;
  total?: number | null;
  comments?: string | null;
  lookup_error?: string;
}

export async function listInvoices(q: {
  partner_id?: string;
  status?: InvoiceStatus;
  user_id?: string;
  search?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}): Promise<{ invoices: AdminInvoice[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return fetchJson(`/admin/invoices${qs ? `?${qs}` : ''}`);
}

export async function getInvoice(
  invoiceId: string,
): Promise<{ invoice: AdminInvoice; provider_state: InvoiceProviderState | null }> {
  return fetchJson(`/admin/invoices/${encodeURIComponent(invoiceId)}`);
}

export async function retryInvoice(
  invoiceId: string,
): Promise<{ invoice_id: string; status: 'retry_enqueued' }> {
  return fetchJson(`/admin/invoices/${encodeURIComponent(invoiceId)}/retry`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
}

export async function resendInvoiceEmail(
  invoiceId: string,
  email?: string,
): Promise<{ invoice_id: string; status: 'email_sent' }> {
  return fetchJson(`/admin/invoices/${encodeURIComponent(invoiceId)}/resend-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(email ? { email } : {}),
  });
}

// ---------------------------------------------------------------------------
// Manual de uso (/docs)
// ---------------------------------------------------------------------------

export interface AdminDocsTicket {
  cookie_name: string;
  value: string;
  expires_at: number;
  max_age: number;
}

/**
 * Pide un pase de lectura para el manual. Ver services/admin-docs-ticket: el
 * manual es un sitio estatico y no puede preguntarle a la API si hay sesion,
 * asi que se le deja la respuesta escrita en una cookie del propio dominio.
 */
export async function adminDocsTicket(): Promise<AdminDocsTicket> {
  return fetchJson<AdminDocsTicket>('/admin/docs/ticket');
}
